/**
 * NurtureStatus Component
 * Shows nurture queue status in LeadDetail
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  EnvelopeIcon,
  CheckCircleIcon,
  ClockIcon,
  PauseIcon,
  PlayIcon,
  XCircleIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';
import leadIntakeService from '../../services/LeadIntakeService';
import toast from 'react-hot-toast';

const NurtureStatus = ({ lead, userId, onRefresh }) => {
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (lead?.id && userId) {
      fetchQueue();
    }
  }, [lead?.id, userId]);

  const fetchQueue = async () => {
    try {
      const result = await leadIntakeService.getNurtureQueue(userId, lead.id);
      setQueue(result.queue);
    } catch (error) {
      console.error('Failed to fetch nurture queue:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async () => {
    if (!queue) return;
    setActionLoading(true);
    try {
      await leadIntakeService.updateNurtureQueueStatus(queue.id, userId, 'paused');
      toast.success('Nurture sequence paused');
      fetchQueue();
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to pause sequence');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    if (!queue) return;
    setActionLoading(true);
    try {
      await leadIntakeService.updateNurtureQueueStatus(queue.id, userId, 'active');
      toast.success('Nurture sequence resumed');
      fetchQueue();
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to resume sequence');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendNow = async () => {
    if (!queue) return;
    setActionLoading(true);
    try {
      const result = await leadIntakeService.sendNextNurtureEmail(queue.id, userId);
      toast.success(`Email sent: "${result.emailSent.subject}"`);
      fetchQueue();
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!queue) return;
    setActionLoading(true);
    try {
      await leadIntakeService.updateNurtureQueueStatus(queue.id, userId, 'cancelled');
      toast.success('Nurture sequence cancelled');
      fetchQueue();
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to cancel sequence');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getTimeUntil = (isoString) => {
    if (!isoString) return '';
    const now = new Date();
    const scheduled = new Date(isoString);
    const diff = scheduled - now;

    if (diff < 0) return 'Due now';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `in ${days} day${days > 1 ? 's' : ''}`;
    } else if (hours > 0) {
      return `in ${hours}h ${minutes}m`;
    } else {
      return `in ${minutes}m`;
    }
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      active: 'bg-green-500/10 text-green-600 dark:text-green-400',
      paused: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
      completed: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      cancelled: 'bg-black/10 dark:bg-white/10 text-black/50 dark:text-white/50'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-[11px] font-medium uppercase ${styles[status] || styles.cancelled}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-black/10 dark:bg-white/10 rounded w-1/3" />
          <div className="h-2 bg-black/10 dark:bg-white/10 rounded w-full" />
          <div className="h-20 bg-black/10 dark:bg-white/10 rounded" />
        </div>
      </div>
    );
  }

  // Hot lead - no nurture
  if (lead?.temperature === 'hot' && !queue) {
    return (
      <div className="bg-gradient-to-r from-orange-500/10 to-transparent rounded-2xl p-6 border border-orange-500/20">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
            <span className="text-lg">🔥</span>
          </div>
          <div>
            <h3 className="text-[15px] font-semibold text-black dark:text-white">Hot Lead</h3>
            <p className="text-[12px] text-black/50 dark:text-white/50">
              No auto-follow up - handle personally
            </p>
          </div>
        </div>
        <p className="text-[13px] text-black/60 dark:text-white/60">
          This lead scored high enough that they're ready to book. Reach out directly for the best results.
        </p>
      </div>
    );
  }

  // No queue found
  if (!queue) {
    return (
      <div className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-black/5 dark:bg-white/5 flex items-center justify-center">
            <EnvelopeIcon className="w-5 h-5 text-black/30 dark:text-white/30" />
          </div>
          <div>
            <h3 className="text-[15px] font-semibold text-black dark:text-white">Auto Follow-up</h3>
            <p className="text-[12px] text-black/50 dark:text-white/50">
              No sequence active
            </p>
          </div>
        </div>
        <p className="text-[13px] text-black/40 dark:text-white/40">
          AI nurturing may be disabled or this lead was created before the feature was enabled.
        </p>
      </div>
    );
  }

  const currentStep = queue.currentStep || 0;
  const sequence = queue.sequence || [];
  const progress = sequence.length > 0 ? (currentStep / sequence.length) * 100 : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center">
            <EnvelopeIcon className="w-5 h-5 text-brand" />
          </div>
          <div>
            <h3 className="text-[15px] font-semibold text-black dark:text-white">Auto Follow-up</h3>
            <p className="text-[12px] text-black/50 dark:text-white/50">
              AI-powered email sequence
            </p>
          </div>
        </div>
        <StatusBadge status={queue.status} />
      </div>

      {/* Progress */}
      <div className="mb-4">
        <div className="flex justify-between text-[12px] mb-2">
          <span className="text-black/60 dark:text-white/60">
            Email {Math.min(currentStep + 1, sequence.length)} of {sequence.length}
          </span>
          {queue.status === 'active' && currentStep < sequence.length && (
            <span className="text-brand font-medium">
              Next {getTimeUntil(sequence[currentStep]?.scheduledAt)}
            </span>
          )}
        </div>
        <div className="h-1.5 bg-black/5 dark:bg-white/5 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
            className="h-full bg-brand rounded-full"
          />
        </div>
      </div>

      {/* Pause Reason */}
      {queue.status === 'paused' && queue.pauseReason && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 text-[13px]">
          {queue.pauseReason === 'lead_became_hot' && '🔥 Paused: Lead became hot'}
          {queue.pauseReason === 'lead_replied' && '💬 Paused: Lead replied'}
          {queue.pauseReason === 'manual' && '⏸️ Paused manually'}
        </div>
      )}

      {/* Sequence Timeline */}
      <div className="space-y-2 mb-4">
        {sequence.map((step, i) => (
          <div
            key={i}
            className={`flex items-center gap-3 py-2 px-3 rounded-lg ${
              step.status === 'sent'
                ? 'bg-green-500/5'
                : step.status === 'pending' && i === currentStep
                ? 'bg-brand/5 border border-brand/20'
                : 'bg-black/[0.02] dark:bg-white/[0.02]'
            }`}
          >
            {step.status === 'sent' ? (
              <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
            ) : step.status === 'pending' && i === currentStep ? (
              <ClockIcon className="w-5 h-5 text-brand flex-shrink-0" />
            ) : step.status === 'skipped' || step.status === 'cancelled' ? (
              <XCircleIcon className="w-5 h-5 text-black/20 dark:text-white/20 flex-shrink-0" />
            ) : (
              <div className="w-5 h-5 rounded-full border-2 border-black/10 dark:border-white/10 flex-shrink-0" />
            )}

            <div className="flex-1 min-w-0">
              <p className={`text-[13px] font-medium truncate ${
                step.status === 'sent'
                  ? 'text-black/70 dark:text-white/70'
                  : step.status === 'pending' && i === currentStep
                  ? 'text-black dark:text-white'
                  : 'text-black/40 dark:text-white/40'
              }`}>
                {step.emailSubject || `Email ${i + 1}`}
              </p>
              <p className="text-[11px] text-black/40 dark:text-white/40">
                {step.status === 'sent'
                  ? `Sent ${formatDate(step.sentAt)}`
                  : step.status === 'pending'
                  ? `Scheduled ${formatDate(step.scheduledAt)}`
                  : step.status}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      {queue.status !== 'completed' && queue.status !== 'cancelled' && (
        <div className="flex gap-2 pt-4 border-t border-black/5 dark:border-white/5">
          {queue.status === 'active' && (
            <>
              <button
                onClick={handlePause}
                disabled={actionLoading}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl
                  border border-black/10 dark:border-white/10 text-black/60 dark:text-white/60
                  hover:bg-black/5 dark:hover:bg-white/5 text-[13px] font-medium transition-colors
                  disabled:opacity-50"
              >
                <PauseIcon className="w-4 h-4" />
                Pause
              </button>
              <button
                onClick={handleSendNow}
                disabled={actionLoading || currentStep >= sequence.length}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl
                  bg-brand text-white text-[13px] font-medium hover:bg-brand-hover
                  transition-colors disabled:opacity-50"
              >
                <PaperAirplaneIcon className="w-4 h-4" />
                Send Now
              </button>
            </>
          )}

          {queue.status === 'paused' && (
            <>
              <button
                onClick={handleCancel}
                disabled={actionLoading}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl
                  border border-red-500/20 text-red-500 hover:bg-red-500/10
                  text-[13px] font-medium transition-colors disabled:opacity-50"
              >
                <XCircleIcon className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={handleResume}
                disabled={actionLoading}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl
                  bg-brand text-white text-[13px] font-medium hover:bg-brand-hover
                  transition-colors disabled:opacity-50"
              >
                <PlayIcon className="w-4 h-4" />
                Resume
              </button>
            </>
          )}
        </div>
      )}

      {/* Completed State */}
      {queue.status === 'completed' && (
        <div className="pt-4 border-t border-black/5 dark:border-white/5">
          <p className="text-[13px] text-center text-black/40 dark:text-white/40">
            Sequence completed. {sequence.filter(s => s.status === 'sent').length} emails sent.
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default NurtureStatus;
